import { getApiUrl } from '../config/api';
import { formatDate } from './dateUtils';
import { isFuturesModeEnabled, getOptionsUnderlying } from './unifiedSymbolMapping';
import type { ContractHistoricalParams, EodRecord, EodTodaySnapshot } from '../types/contractHistorical';
import {
  EOD_MAX_PAGES,
  EOD_PAGE_FETCH_CONCURRENCY,
  EOD_PAGE_LIMIT,
  MAX_RANGE_DAYS,
} from '../types/contractHistorical';

const API_BASE = typeof getApiUrl === 'function' ? getApiUrl('/api/historical/eod') : '/api/historical/eod';

export function thirtyDaysAgo(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

export function resolveFuturesUnderlying(sym: string): string {
  if (!isFuturesModeEnabled()) return sym;
  const etf = getOptionsUnderlying(sym);
  return etf !== sym ? etf : sym;
}

export function clampDateRange(from: string, to: string): { from: string; to: string } {
  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  const minDate = new Date(today);
  minDate.setDate(minDate.getDate() - MAX_RANGE_DAYS);
  const minFromStr = minDate.toISOString().slice(0, 10);
  let f = from || minFromStr;
  let t = to || todayStr;
  if (t > todayStr) t = todayStr;
  if (f < minFromStr) f = minFromStr;
  if (f > t) f = t;
  const fromDate = new Date(f);
  const toDate = new Date(t);
  const daysDiff = Math.round((toDate.getTime() - fromDate.getTime()) / (24 * 60 * 60 * 1000));
  if (daysDiff > MAX_RANGE_DAYS) {
    const newFrom = new Date(toDate);
    newFrom.setDate(newFrom.getDate() - MAX_RANGE_DAYS);
    f = newFrom.toISOString().slice(0, 10);
  }
  return { from: f, to: t };
}

export function buildEodUrl(
  p: ContractHistoricalParams,
  fromDate?: string,
  toDate?: string,
  typeOverride?: 'call' | 'put',
  pageOffset = 0,
): string {
  const base = API_BASE.startsWith('http') ? API_BASE : `${window.location.origin}${API_BASE}`;
  const u = new URL(base);
  u.searchParams.set('underlying_symbol', resolveFuturesUnderlying(p.underlying));
  u.searchParams.set('strike_eq', String(p.strike));
  if (p.expiry && p.expiry !== 'all') u.searchParams.set('exp_date_eq', p.expiry);
  const type = typeOverride ?? (p.optionType?.toLowerCase() === 'put' ? 'put' : 'call');
  u.searchParams.set('type', type);
  const to = toDate || new Date().toISOString().slice(0, 10);
  const from = fromDate || thirtyDaysAgo();
  u.searchParams.set('tradetime_from', from);
  u.searchParams.set('tradetime_to', to);
  u.searchParams.set('page_limit', String(EOD_PAGE_LIMIT));
  u.searchParams.set('page_offset', String(pageOffset));
  return u.toString();
}

export function buildTapeByDayUrl(
  p: ContractHistoricalParams,
  from: string,
  to: string,
  maxDays = 30,
): string | null {
  if (!p.underlying || p.strike == null || !p.expiry || p.expiry === 'all') return null;
  const base = typeof getApiUrl === 'function' ? getApiUrl('/api/historical/contract-tape-by-day') : '/api/historical/contract-tape-by-day';
  const url = base.startsWith('http') ? base : `${window.location.origin}${base}`;
  const u = new URL(url);
  u.searchParams.set('underlying_symbol', resolveFuturesUnderlying(p.underlying));
  u.searchParams.set('strike_eq', String(p.strike));
  const expEq = formatDate(p.expiry) || p.expiry;
  if (expEq && /^\d{4}-\d{2}-\d{2}$/.test(expEq)) u.searchParams.set('exp_date_eq', expEq);
  const typeVal = (p.optionType || 'call').toString().toLowerCase().startsWith('p') ? 'put' : 'call';
  u.searchParams.set('type', typeVal);
  const cl = clampDateRange(from, to);
  u.searchParams.set('tradetime_from', cl.from);
  u.searchParams.set('tradetime_to', cl.to);
  u.searchParams.set('max_days', String(Math.max(1, Math.min(maxDays, 62))));
  return u.toString();
}

export function buildIntradayUrl(
  p: ContractHistoricalParams,
  optionType: 'call' | 'put',
  intradayDate: string,
  fallbackToDate?: string,
): string {
  const base = typeof getApiUrl === 'function' ? getApiUrl('/api/historical/intraday-gamma-shift') : '/api/historical/intraday-gamma-shift';
  const url = base.startsWith('http') ? base : `${window.location.origin}${base}`;
  const u = new URL(url);
  u.searchParams.set('underlying', resolveFuturesUnderlying(p.underlying || 'SPY'));
  const dateForRequest = intradayDate || fallbackToDate || new Date().toISOString().slice(0, 10);
  u.searchParams.set('date', dateForRequest);
  if (p.strike != null) u.searchParams.set('strike_eq', String(p.strike));
  const rawExpiry = p.expiry && p.expiry !== 'all' ? p.expiry : '';
  const expNorm = rawExpiry ? formatDate(rawExpiry) : '';
  if (expNorm && /^\d{4}-\d{2}-\d{2}$/.test(expNorm)) u.searchParams.set('exp_date_eq', expNorm);
  if (p.strike != null && expNorm) u.searchParams.set('type', optionType);
  u.searchParams.set('strict_date', '1');
  return u.toString();
}

export function parseEodResponse(json: unknown): EodRecord[] {
  const j = json as { error?: unknown; data?: unknown[] };
  if (j?.error) return [];
  const raw = j?.data || [];
  const list = raw.map((d: unknown) => {
    const row = d as { attributes?: EodRecord } & EodRecord;
    return row.attributes || row;
  });
  list.sort((a: EodRecord, b: EodRecord) => (a.tradetime || '').localeCompare(b.tradetime || ''));
  return list;
}

export async function fetchEodPaginated(
  params: ContractHistoricalParams,
  from: string,
  to: string,
  typeOverride: 'call' | 'put',
  maxPages: number = EOD_MAX_PAGES,
): Promise<{ list: EodRecord[]; serverError?: string; todaySnapshot?: EodTodaySnapshot | null }> {
  const clamped = clampDateRange(from, to);
  let all: EodRecord[] = [];
  let serverError: string | undefined;
  let todaySnapshot: EodTodaySnapshot | null | undefined;
  const limit = Math.max(1, Math.min(maxPages, EOD_MAX_PAGES));
  const eodTimeoutPerPageMs = 70_000;

  const fetchOneEodPage = async (
    pageIndex: number,
  ): Promise<{ pageIndex: number; list: EodRecord[]; ok: boolean; serverError?: string; todaySnapshot?: EodTodaySnapshot | null }> => {
    const url = buildEodUrl(params, clamped.from, clamped.to, typeOverride, pageIndex * EOD_PAGE_LIMIT);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), eodTimeoutPerPageMs);
    try {
      let r: Response;
      let json: unknown = {};
      try {
        r = await fetch(url, { signal: controller.signal });
        json = r.ok ? await r.json() : await r.json().catch(() => ({}));
      } catch (e) {
        const msg =
          (e as Error)?.name === 'AbortError' ? 'Request timed out. Try again later.' : 'Unable to load historical data.';
        return { pageIndex, list: [], ok: false, serverError: msg };
      }
      const list = parseEodResponse(json);
      // Extract today_snapshot from top-level response (injected by backend)
      let snapshot: EodTodaySnapshot | null | undefined;
      if (json && typeof json === 'object') {
        const j = json as Record<string, unknown>;
        if (j.today_snapshot && typeof j.today_snapshot === 'object') {
          snapshot = j.today_snapshot as EodTodaySnapshot;
        }
      }
      if (!r.ok) {
        const j = json as Record<string, string>;
        const err =
          r.status >= 502 && r.status <= 504
            ? 'Request timed out or server busy. Try again later.'
            : j?.message || j?.detail || j?.hint || j?.error || 'No data for this symbol/date.';
        return { pageIndex, list, ok: false, serverError: err, todaySnapshot: snapshot };
      }
      return { pageIndex, list, ok: true, todaySnapshot: snapshot };
    } finally {
      clearTimeout(timeoutId);
    }
  };

  try {
    const first = await fetchOneEodPage(0);
    all = all.concat(first.list);
    if (first.todaySnapshot) todaySnapshot = first.todaySnapshot;
    if (!first.ok) {
      serverError = first.serverError;
      all.sort((a, b) => (a.tradetime || '').localeCompare(b.tradetime || ''));
      return { list: all.length ? all : [], serverError, todaySnapshot };
    }
    if (first.list.length < EOD_PAGE_LIMIT) {
      all.sort((a, b) => (a.tradetime || '').localeCompare(b.tradetime || ''));
      return { list: all, serverError, todaySnapshot };
    }

    let start = 1;
    while (start < limit) {
      const waveEnd = Math.min(start + EOD_PAGE_FETCH_CONCURRENCY, limit);
      const indices: number[] = [];
      for (let p = start; p < waveEnd; p += 1) indices.push(p);
      const wave = await Promise.all(indices.map((p) => fetchOneEodPage(p)));
      wave.sort((a, b) => a.pageIndex - b.pageIndex);
      let stop = false;
      for (const res of wave) {
        all = all.concat(res.list);
        if (res.todaySnapshot && !todaySnapshot) todaySnapshot = res.todaySnapshot;
        if (!res.ok) {
          serverError = res.serverError ?? serverError;
          stop = true;
          break;
        }
        if (res.list.length < EOD_PAGE_LIMIT) {
          stop = true;
          break;
        }
      }
      if (stop) break;
      start = waveEnd;
    }
  } catch {
    return { list: all, serverError: serverError || 'Unable to load historical data.', todaySnapshot };
  }
  all.sort((a, b) => (a.tradetime || '').localeCompare(b.tradetime || ''));
  return { list: all, serverError, todaySnapshot };
}
